import { ensureRequestAccess } from './access';
import { getApiErrorMessage, notifyApiError } from 'utils/apiError';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
const inFlightGetRequests = new Map();

function buildUrl(path, query) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = API_BASE_URL
    ? new URL(`${API_BASE_URL}${normalizedPath}`)
    : new URL(normalizedPath, window.location.origin);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'All') {
        url.searchParams.set(key, value);
      }
    });
  }

  return url.toString();
}

export async function apiRequest(path, options = {}) {
  const { query, headers, body, silentError = false, timeoutMs, ...fetchOptions } = options;
  const token = localStorage.getItem('authToken');
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const method = (fetchOptions.method || 'GET').toUpperCase();

  try {
    await ensureRequestAccess(path, fetchOptions);
  } catch (error) {
    if (!silentError) notifyApiError(error, 'You do not have permission to perform this action');
    throw error;
  }

  const url = buildUrl(path, query);
  const requestOptions = {
    ...fetchOptions,
    headers: {
      Accept: 'application/json',
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      'X-User-Id': localStorage.getItem('userId') || 'ui-user',
      'X-Employee-Id': localStorage.getItem('employeeId') || '',
      'X-User-Role': localStorage.getItem('userRole') || 'Employee',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers
    },
    body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body)
  };

  let timeoutId;
  if (timeoutMs && typeof AbortController !== 'undefined' && !requestOptions.signal) {
    const controller = new AbortController();
    requestOptions.signal = controller.signal;
    timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  }

  if (method === 'GET' && body === undefined) {
    const key = `${url}|${localStorage.getItem('userId') || ''}|${token || ''}`;
    if (inFlightGetRequests.has(key)) return inFlightGetRequests.get(key);

    const request = performFetch(url, requestOptions, { silentError }).finally(() => {
      if (timeoutId) window.clearTimeout(timeoutId);
      inFlightGetRequests.delete(key);
    });
    inFlightGetRequests.set(key, request);
    return request;
  }

  return performFetch(url, requestOptions, { silentError }).finally(() => {
    if (timeoutId) window.clearTimeout(timeoutId);
  });
}

async function performFetch(url, requestOptions, { silentError = false } = {}) {
  let response;
  try {
    response = await fetch(url, requestOptions);
  } catch (cause) {
    const fallbackMessage = cause?.name === 'AbortError'
      ? 'Request timed out. Please try again.'
      : 'Unable to connect to the server';
    const error = new Error(getApiErrorMessage(cause, fallbackMessage));
    error.cause = cause;
    if (!silentError) notifyApiError(error);
    throw error;
  }

  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json() : await response.text();

  if (!response.ok) {
    const message = getApiErrorMessage(data, `Request failed with status ${response.status}`);
    const error = new Error(message || `Request failed with status ${response.status}`);
    error.status = response.status;
    error.data = data;
    if (!silentError) notifyApiError(error);
    throw error;
  }

  return data;
}

export { API_BASE_URL };
